import { produce } from "immer";
import React, { useCallback, useContext, useEffect, useState } from "react";

import { languageMeta } from "@examspark/common/dist/meta/assets";
import { CountOption, SelectInput } from "@examspark/common-ui";
import { BucketAggregation } from "@examspark/types";

import { NewActivityContext } from "../../../../classroom";
import { useExamBuilderSearch } from "../hooks/useSearch";

export const getSectionsForSearch = (sections: string[]) => {
  return {
    or: sections.map((section) => ({
      scope: `${section.replace("section#", "")}`,
    })),
  };
};

const LanguageSelect: React.FC<React.PropsWithChildren<unknown>> = () => {
  const [state, dispatchState] = useContext(NewActivityContext);
  const [facets, setFacets] = useState<BucketAggregation[]>();
  const search = useExamBuilderSearch();
  const { searchItems } = search;

  const fetchLanguages = useCallback(async () => {
    if (!state.document || !state.sections) return;

    const results = await searchItems({
      filter: {
        scopes: state.sections.map(
          (section) => `${section.replace("section#", "")}`,
        ),
      },
    });

    const aggs: BucketAggregation[] = results?.aggregations?.language?.buckets;
    setFacets(aggs);
    if (!state.language && aggs.length > 0) {
      if (aggs[0].key !== state.language) {
        dispatchState({
          type: "UPDATE",
          data: produce(state, (draft) => {
            draft.language = aggs[0].key;
            draft.objectives = [];
          }),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.sections]);

  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages, state.document, state.sections]);

  // console.log("lang facets", facets);

  return (
    <>
      <SelectInput
        isClearable
        components={{ Option: CountOption as any }}
        disabled={!state.document || !state.sections}
        placeholder="Select Language..."
        value={state.language}
        onChange={(selected) => {
          if (selected?.value === state.language) return;
          dispatchState({
            type: "UPDATE",
            data: produce(state, (draft) => {
              draft.language = selected?.value;
              draft.objectives = [];
            }),
          });
        }}
        options={
          facets?.reduce((state: any, lang) => {
            const _lang = languageMeta[lang.key as keyof typeof languageMeta];
            if (!_lang) return state;

            state.push({
              value: lang.key,
              label: `${_lang.label}`,
              count: lang.doc_count,
            });
            return state;
          }, []) || []
        }
      />
    </>
  );
};

export default LanguageSelect;
